
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Navigation } from "@/components/layout/Navigation";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { useClassDetails } from "@/hooks/useClassDetails";
import { useEnrolledUsers } from "@/hooks/useEnrolledUsers";
import { ClassDetailsCard } from "@/components/attendance/ClassDetailsCard";
import { DateSelector } from "@/components/attendance/DateSelector";
import { AttendanceContainer } from "@/components/attendance/AttendanceContainer";
import { Loading } from "@/components/attendance/Loading";

const GestionAsistencia = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { data: profile, isLoading: profileLoading } = useProfileQuery();

  useEffect(() => {
    // Verificar si el usuario tiene permisos para acceder a esta página
    if (!profileLoading && profile && !(profile.role === 'profesor')) {
      toast({
        title: "Acceso denegado",
        description: "No tienes permisos para acceder a esta página",
        variant: "destructive",
      });
      navigate('/clases');
    }
  }, [profile, profileLoading, navigate]);

  // Obtener detalles de la clase
  const { data: classDetails, isLoading: classLoading } = useClassDetails(classId);

  // Obtener la fecha actual formateada como YYYY-MM-DD
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);

  // Obtener usuarios inscritos y sus registros de asistencia para la fecha seleccionada
  const { data: enrolledUsers, isLoading: usersLoading } = useEnrolledUsers(classId, selectedDate);

  // Verificar que el profesor sea el instructor de la clase
  useEffect(() => {
    if (!profileLoading && !classLoading && profile && classDetails) {
      if (profile.role === 'profesor') {
        const instructorName = `${profile.first_name} ${profile.last_name}`;
        if (classDetails.instructor !== instructorName) {
          toast({
            title: "Acceso denegado",
            description: "Solo puedes gestionar asistencia de tus propias clases",
            variant: "destructive",
          });
          navigate('/clases');
        }
      }
    }
  }, [profile, profileLoading, classDetails, classLoading, navigate]);

  const isLoading = profileLoading || classLoading || usersLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-primary pb-24">
        <main className="container pt-16 px-4">
          <Loading />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-primary pb-24">
      <main className="container pt-16 px-4">
        <div className="mb-6 flex items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/clases')}
            className="mr-2 text-primary hover:bg-primary/10"
          >
            <ArrowLeft className="h-5 w-5 mr-1 text-primary" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-primary">Gestión de Asistencia</h1>
        </div>

        {classDetails && (
          <ClassDetailsCard classDetails={classDetails} />
        )}

        <DateSelector 
          selectedDate={selectedDate} 
          onDateChange={setSelectedDate} 
        />

        <AttendanceContainer 
          enrolledUsers={enrolledUsers} 
          classId={classId} 
          selectedDate={selectedDate}
        />
        
        <Navigation />
      </main>
    </div>
  );
};

export default GestionAsistencia;
